import React, { useState, useMemo } from 'react';
import { auth } from '../data/auth';
import BackupRestore from './BackupRestore';
import AutoBackupPanel from './AutoBackupPanel';
import ApiSettings from './ApiSettings';
import ParamManager from './ParamManager';
import { approvals, db } from '../data/store';
import { syncedSet, syncedGet, SERVER_URL, clearCacheKeys, pauseSync, syncAllFromServer } from '../data/serverStore';

const LOGO_PLACEHOLDER = null;

function RoleBadge({ role }) {
  const preset = auth.ROLE_PRESETS[role] || auth.ROLE_PRESETS.custom;
  return (
    <span style={{background: preset.color+'22', color: preset.color,
      padding:'2px 9px', borderRadius:10, fontSize:11, fontWeight:700}}>
      {preset.label}
    </span>
  );
}

function PermissionGrid({ screens, onChange, disabled }) {
  return (
    <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',
      gap:6, marginTop:8}}>
      {auth.ALL_SCREENS.map(s => {
        const allowed = screens.includes(s.id);
        return (
          <label key={s.id} style={{display:'flex', alignItems:'center', gap:8,
            padding:'6px 10px', borderRadius:5, cursor: disabled?'default':'pointer',
            border:`1px solid ${allowed?'#1e3060':'#e0e4ec'}`,
            background: allowed ? '#f0f4ff' : '#fafafa',
            opacity: disabled ? 0.6 : 1, userSelect:'none'}}>
            <input type="checkbox" checked={allowed} disabled={disabled}
              onChange={e => {
                if (disabled) return;
                const next = e.target.checked
                  ? [...screens, s.id]
                  : screens.filter(x => x !== s.id);
                onChange(next);
              }}
              style={{accentColor:'#1e3060', width:14, height:14}}/>
            <span style={{fontSize:12}}>{s.icon} {s.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function UserModal({ user, onSave, onClose, currentUserId }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState({
    username:    user?.username    || '',
    displayName: user?.displayName || '',
    password:    '',
    confirmPw:   '',
    role:        user?.role        || 'technician',
    screens:     user?.screens     || [...(auth.ROLE_PRESETS.technician.screens)],
    active:      user?.active !== false,
  });
  const [error, setError] = useState('');
  const [genPw, setGenPw] = useState('');
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#';
    let pw = '';
    for (let i=0;i<10;i++) pw += chars[Math.floor(Math.random()*chars.length)];
    setGenPw(pw);
    setF('password', pw);
    setF('confirmPw', pw);
  };

  const applyRole = (role) => {
    setF('role', role);
    if (role !== 'custom') setF('screens', [...(auth.ROLE_PRESETS[role]?.screens || [])]);
  };

  const handleSave = () => {
    if (!form.username.trim()) { setError('Username is required'); return; }
    if (!isEdit && !form.password) { setError('Password is required'); return; }
    if (form.password && form.password !== form.confirmPw) {
      setError('Passwords do not match'); return;
    }
    if (form.password && form.password.length < 6) {
      setError('Password must be at least 6 characters'); return;
    }
    onSave(form);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:680}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `✏️ Edit User — ${user.username}` : '➕ Create New User'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="notice" style={{background:'#fff0f0',borderLeft:'3px solid #b83232',
              color:'#b83232',marginBottom:12}}>{error}</div>
          )}

          {/* Basic info */}
          <div className="form-section-title">Account Details</div>
          <div className="form-grid" style={{marginBottom:14}}>
            <div className="form-group">
              <label>Username *</label>
              <input value={form.username} onChange={e=>setF('username',e.target.value)}
                placeholder="e.g. jsmith"
                disabled={isEdit && user.id === currentUserId}/>
            </div>
            <div className="form-group">
              <label>Display Name</label>
              <input value={form.displayName} onChange={e=>setF('displayName',e.target.value)}
                placeholder="e.g. John Smith"/>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.active?'active':'inactive'}
                onChange={e=>setF('active',e.target.value==='active')}>
                <option value="active">Active</option>
                <option value="inactive">Inactive (locked out)</option>
              </select>
            </div>
          </div>

          {/* Password */}
          <div className="form-section-title">
            {isEdit ? 'Change Password (leave blank to keep current)' : 'Set Password *'}
          </div>
          <div className="form-grid" style={{marginBottom:14}}>
            <div className="form-group">
              <label>Password</label>
              <input type="password" value={form.password}
                onChange={e=>setF('password',e.target.value)}
                placeholder={isEdit?'Leave blank to keep current':'Min 6 characters'}/>
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" value={form.confirmPw}
                onChange={e=>setF('confirmPw',e.target.value)}
                placeholder="Repeat password"/>
            </div>
            <div className="form-group" style={{justifyContent:'flex-end'}}>
              <label>&nbsp;</label>
              <button className="btn" onClick={generatePassword}
                style={{background:'#f0f4ff',borderColor:'#1e3060',color:'#1e3060'}}>
                🎲 Generate Password
              </button>
            </div>
          </div>
          {genPw && (
            <div style={{background:'#e8f4ff',border:'1px solid #aac8ee',borderRadius:4,
              padding:'8px 14px',marginBottom:12,fontSize:12,display:'flex',
              alignItems:'center',gap:10}}>
              <span>Generated password:</span>
              <strong style={{fontFamily:'monospace',fontSize:14,color:'#1e3060',
                letterSpacing:'1px'}}>{genPw}</strong>
              <span style={{color:'#888',fontSize:11}}>— copy before closing</span>
            </div>
          )}

          {/* Role */}
          <div className="form-section-title">Role</div>
          <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
            {Object.entries(auth.ROLE_PRESETS).map(([key,preset])=>(
              <button key={key} onClick={()=>applyRole(key)}
                style={{padding:'6px 14px',borderRadius:6,border:'2px solid',
                  fontSize:12,fontWeight:600,cursor:'pointer',
                  borderColor:form.role===key?preset.color:'#e0e4ec',
                  background:form.role===key?preset.color+'22':'white',
                  color:form.role===key?preset.color:'#555'}}>
                {preset.label}
              </button>
            ))}
          </div>

          {/* Screen permissions */}
          <div className="form-section-title">
            Screen Access
            <span style={{marginLeft:8,fontSize:11,fontWeight:400,color:'#888'}}>
              {form.screens.length} of {auth.ALL_SCREENS.length} screens allowed
            </span>
          </div>
          <PermissionGrid
            screens={form.screens}
            onChange={screens=>{ setF('screens',screens); setF('role','custom'); }}
            disabled={false}
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={handleSave}>
            {isEdit ? '💾 Save Changes' : '➕ Create User'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel({ session, toast }) {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // null | 'create' | user object
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  const refresh = () => auth.getUsersAsync().then(u => setUsers(u || []));
  React.useEffect(() => { refresh(); }, []);

  const filtered = useMemo(()=>{
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      u.username.toLowerCase().includes(q) ||
      u.displayName?.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  },[users, search]);

  const handleSave = async (form) => {
    if (modal?.id) {
      await auth.updateUser(modal.id, {
        username: form.username,
        displayName: form.displayName,
        role: form.role,
        screens: form.screens,
        active: form.active,
        ...(form.password ? { password: form.password } : {}),
      });
      toast('success', `User ${form.username} updated`);
    } else {
      const result = await auth.addUser(form);
      if (result.error) { toast('error', result.error); return; }
      toast('success', `User ${form.username} created`);
    }
    setModal(null);
    refresh();
  };

  const handleDelete = async (user) => {
    if (user.id === session.userId) { toast('error', "Can't delete your own account"); return; }
    await auth.deleteUser(user.id);
    toast('success', `User ${user.username} deleted`);
    setConfirmDelete(null);
    refresh();
  };

  const tabStyle = (id) => ({
    background:'none', border:'none',
    color: activeTab===id ? '#7ec8e3' : '#99aac8',
    padding:'9px 16px', cursor:'pointer', fontSize:12, fontWeight:600,
    borderBottom: activeTab===id ? '2px solid #7ec8e3' : '2px solid transparent',
  });

  return (
    <>
      {/* Sub-tabs */}
      <div style={{background:'#1e3060',display:'flex',padding:'0 12px',flexShrink:0}}>
        <button style={tabStyle('users')} onClick={()=>setActiveTab('users')}>
          👥 User Accounts
        </button>
        <button style={tabStyle('permissions')} onClick={()=>setActiveTab('permissions')}>
          🔒 Screen Permissions
        </button>
        <button style={tabStyle('security')} onClick={()=>setActiveTab('security')}>
          🔑 My Password
        </button>
        <button style={tabStyle('backup')} onClick={()=>setActiveTab('backup')}>
          💾 Backup & Restore
        </button>
        <button style={tabStyle('autobackup')} onClick={()=>setActiveTab('autobackup')}>
          🔄 Auto-Backup
        </button>
        <button style={tabStyle('apisettings')} onClick={()=>setActiveTab('apisettings')}>
          🔑 API Settings
        </button>
        <button style={tabStyle('params')} onClick={()=>setActiveTab('params')}>
          🧪 Test Parameters
        </button>
        <button style={tabStyle('settings')} onClick={()=>setActiveTab('settings')}>
          ⚙️ Settings & Reset
        </button>
        <button style={tabStyle('approvals')} onClick={()=>setActiveTab('approvals')}>
          📋 Approvals
          {(approvals?.getCount?.()??0)>0 && (
            <span style={{marginLeft:4,background:'#b83232',color:'white',
              borderRadius:10,padding:'1px 7px',fontSize:10,fontWeight:700}}>
              {approvals?.getCount?.()??0}
            </span>
          )}
        </button>
      </div>

      {/* ── USERS TAB ── */}
      {activeTab==='users' && (
        <>
          <div className="toolbar">
            <button className="btn btn-primary" onClick={()=>setModal({})}>
              ➕ Create New User
            </button>
            <div className="tb-sep"/>
            <input className="search-input"
              placeholder="Search username, name, role..."
              value={search} onChange={e=>setSearch(e.target.value)} style={{width:260}}/>
            <div className="tb-sep"/>
            <span className="tb-label">Total users:</span>
            <span className="tb-value">{users.length}</span>
            <span className="tb-label" style={{marginLeft:8}}>Active:</span>
            <span className="tb-value">{users.filter(u=>u.active).length}</span>
          </div>

          <div className="content">
            <div className="panel">
              <div className="panel-header">👥 User Accounts ({filtered.length})</div>
              <div style={{overflowY:'auto',maxHeight:'calc(100vh - 220px)'}}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Display Name</th>
                      <th>Role</th>
                      <th>Screens</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u=>(
                      <tr key={u.id}>
                        <td>
                          <strong className="mono">{u.username}</strong>
                          {u.id===session.userId && (
                            <span style={{marginLeft:6,fontSize:9,background:'#1e3060',
                              color:'white',borderRadius:8,padding:'1px 5px'}}>YOU</span>
                          )}
                        </td>
                        <td>{u.displayName}</td>
                        <td><RoleBadge role={u.role}/></td>
                        <td>
                          <span style={{fontSize:11,color:'#666'}}>
                            {u.screens.length} / {auth.ALL_SCREENS.length} screens
                          </span>
                          <div style={{display:'flex',flexWrap:'wrap',gap:2,marginTop:3}}>
                            {auth.ALL_SCREENS.filter(s=>u.screens.includes(s.id)).map(s=>(
                              <span key={s.id} style={{fontSize:9,background:'#f0f4ff',
                                color:'#1e3060',borderRadius:4,padding:'1px 5px'}}>
                                {s.icon}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span style={{background:u.active?'#d4edda':'#f8d7da',
                            color:u.active?'#155724':'#721c24',
                            padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:700}}>
                            {u.active?'Active':'Inactive'}
                          </span>
                        </td>
                        <td style={{fontSize:11,color:'#888'}}>
                          {u.lastLogin
                            ? new Date(u.lastLogin).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td style={{fontSize:11,color:'#888'}}>
                          {new Date(u.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{display:'flex',gap:4}}>
                          <button className="btn btn-sm"
                            onClick={()=>setModal(u)}>✏️ Edit</button>
                          {u.id!==session.userId && (
                            <button className="btn btn-sm"
                              style={{color:'#b83232'}}
                              onClick={()=>setConfirmDelete(u)}>🗑</button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filtered.length===0 && (
                      <tr><td colSpan={8} className="text-center text-muted"
                        style={{padding:24}}>No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── PERMISSIONS TAB ── */}
      {activeTab==='permissions' && (
        <div className="content">
          <div className="notice notice-info" style={{marginBottom:12}}>
            Overview of what each user can access. Click Edit on a user to change their permissions.
          </div>
          <div style={{overflowX:'auto'}}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  {auth.ALL_SCREENS.map(s=>(
                    <th key={s.id} style={{textAlign:'center',fontSize:10,
                      minWidth:64,padding:'6px 4px'}}>
                      {s.icon}<br/>{s.label.split(' ')[0]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u=>(
                  <tr key={u.id}>
                    <td>
                      <strong>{u.username}</strong>
                      {!u.active&&<span style={{marginLeft:4,fontSize:9,color:'#b83232'}}>(inactive)</span>}
                    </td>
                    <td><RoleBadge role={u.role}/></td>
                    {auth.ALL_SCREENS.map(s=>(
                      <td key={s.id} style={{textAlign:'center',padding:'6px 4px'}}>
                        {u.screens.includes(s.id)
                          ? <span style={{color:'#1a6b42',fontSize:16}}>✓</span>
                          : <span style={{color:'#e0e4ec',fontSize:14}}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MY PASSWORD TAB ── */}
      {activeTab==='security' && (
        <div className="content">
          <div className="panel" style={{maxWidth:460}}>
            <div className="panel-header">🔑 Change My Password</div>
            <div className="panel-body">
              <ChangePasswordForm session={session} toast={toast}/>
            </div>
          </div>
        </div>
      )}

      {/* ── TEST PARAMETERS TAB ── */}
      {activeTab==='params' && (
        <ParamManager toast={toast}/>
      )}

      {/* ── API SETTINGS TAB ── */}
      {activeTab==='apisettings' && (
        <ApiSettings toast={toast}/>
      )}

      {/* ── AUTO-BACKUP TAB ── */}
      {activeTab==='autobackup' && (
        <AutoBackupPanel toast={toast}/>
      )}

      {/* ── BACKUP TAB ── */}
      {activeTab==='backup' && (
        <BackupRestore session={session} toast={toast}/>
      )}

      {/* ── SETTINGS TAB ── */}
      {activeTab==='settings' && (
        <SettingsTab toast={toast}/>
      )}

      {/* ── APPROVALS TAB ── */}
      {activeTab==='approvals' && (
        <ApprovalsTab session={session} toast={toast}/>
      )}

      {/* User create/edit modal */}
      {modal!==null && (
        <UserModal
          user={modal?.id ? modal : null}
          onSave={handleSave}
          onClose={()=>setModal(null)}
          currentUserId={session.userId}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={()=>setConfirmDelete(null)}>
          <div className="modal" style={{width:400}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>🗑 Delete User</h2>
              <button className="modal-close" onClick={()=>setConfirmDelete(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom:12}}>
                Are you sure you want to delete user <strong>{confirmDelete.username}</strong>?
                This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger"
                onClick={()=>handleDelete(confirmDelete)}>
                🗑 Yes, Delete
              </button>
              <button className="btn" onClick={()=>setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ChangePasswordForm({ session, toast }) {
  const [form, setForm] = useState({current:'',next:'',confirm:''});
  const [error, setError] = useState('');
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleSave = async () => {
    setError('');
    if (!form.current) { setError('Enter current password'); return; }
    if (!form.next) { setError('Enter new password'); return; }
    if (form.next.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (form.next !== form.confirm) { setError('Passwords do not match'); return; }
    // Verify current password
    const users = await auth.getUsersAsync();
    const me = users.find(u=>u.id===session.userId);
    if (!me || me.passwordHash !== auth.hashPassword(form.current)) {
      setError('Current password is incorrect'); return;
    }
    auth.resetPassword(session.userId, form.next);
    toast('success', 'Password changed successfully');
    setForm({current:'',next:'',confirm:''});
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      {error && (
        <div className="notice" style={{background:'#fff0f0',borderLeft:'3px solid #b83232',
          color:'#b83232'}}>{error}</div>
      )}
      <div className="form-group">
        <label>Current Password</label>
        <input type="password" value={form.current}
          onChange={e=>setF('current',e.target.value)}/>
      </div>
      <div className="form-group">
        <label>New Password (min 6 characters)</label>
        <input type="password" value={form.next}
          onChange={e=>setF('next',e.target.value)}/>
      </div>
      <div className="form-group">
        <label>Confirm New Password</label>
        <input type="password" value={form.confirm}
          onChange={e=>setF('confirm',e.target.value)}/>
      </div>
      <button className="btn btn-primary" onClick={handleSave}
        style={{alignSelf:'flex-start'}}>
        🔑 Change Password
      </button>
    </div>
  );
}

// ── ApprovalsTab ──────────────────────────────────────────────────────────────
function ApprovalsTab({ session, toast }) {
  const [reqs, setReqs] = React.useState(()=>approvals.getAll());
  const [filter, setFilter] = React.useState('pending');
  const [reviewModal, setReviewModal] = React.useState(null);
  const [reviewNote, setReviewNote] = React.useState('');

  const refresh = () => setReqs(approvals.getAll());

  const filtered = reqs.filter(r => filter==='all' ? true : r.status===filter);

  const handleApprove = async (req) => {
    try {
      // Apply the requested record-level operation. Identity changes and
      // deletions cannot be treated as an ordinary sample PATCH: they have
      // dedicated concurrent-safe APIs that move linked records or remove
      // the requested sample without replacing the full samples collection.
      if (req.changeType === 'delete_sample' && req.sampleId) {
        await db.deleteSample(req.sampleId);
      } else if (req.changeType === 'edit_identity' && req.sampleId) {
        const nextId = String(req.proposedChanges?.sampleId || req.sampleId);
        const nextWorkOrder = String(req.proposedChanges?.workOrder || '');
        const changes = { ...(req.proposedChanges || {}) };
        delete changes.sampleId;
        await db.updateSampleIdentity(req.sampleId, nextId, nextWorkOrder);
        await db.updateSample(nextId, changes);
      } else if (req.proposedChanges && req.sampleId) {
        const changes = { ...req.proposedChanges };
        delete changes.sampleId;
        await db.updateSample(req.sampleId, changes);
      }
      approvals.approve(req.id, session.displayName, reviewNote);
      toast('success', `Change approved for Sample ${req.sampleId}`);
      setReviewModal(null);
      setReviewNote('');
      refresh();
    } catch (error) {
      toast('error', error.message || `Could not approve change for Sample ${req.sampleId}`);
    }
  };

  const handleReject = (req) => {
    approvals.reject(req.id, session.displayName, reviewNote);
    toast('info', `Change request rejected`);
    setReviewModal(null); setReviewNote(''); refresh();
  };

  const statusBadge = (s) => {
    const map = {
      pending:  {bg:'#fff3cd',col:'#856404',label:'Pending'},
      approved: {bg:'#d4edda',col:'#155724',label:'Approved'},
      rejected: {bg:'#f8d7da',col:'#721c24',label:'Rejected'},
    };
    const m = map[s] || map.pending;
    return <span style={{background:m.bg,color:m.col,padding:'2px 9px',
      borderRadius:10,fontSize:11,fontWeight:700}}>{m.label}</span>;
  };

  return (
    <>
      <div className="toolbar">
        {[['pending','⏳ Pending'],['approved','✅ Approved'],['rejected','❌ Rejected'],['all','All']].map(([f,l])=>(
          <button key={f} className={`btn ${filter===f?'btn-primary':''}`}
            onClick={()=>setFilter(f)}>
            {l}
            {f==='pending' && (approvals?.getCount?.()??0)>0 && (
              <span style={{marginLeft:4,background:'rgba(255,255,255,0.25)',
                borderRadius:8,padding:'1px 5px',fontSize:10}}>
                {approvals?.getCount?.()??0}
              </span>
            )}
          </button>
        ))}
        <div className="tb-sep"/>
        <span className="tb-label">Total requests:</span>
        <span className="tb-value">{reqs.length}</span>
      </div>

      <div className="content">
        {filtered.length===0 ? (
          <div style={{textAlign:'center',padding:40,color:'#999',fontSize:14}}>
            {filter==='pending' ? '🎉 No pending approval requests' : 'No requests found'}
          </div>
        ) : (
          <div className="panel">
            <div className="panel-header">📋 Change Requests ({filtered.length})</div>
            <div style={{overflowY:'auto',maxHeight:'calc(100vh - 220px)'}}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Sample #</th>
                    <th>Requested By</th>
                    <th>Change Type</th>
                    <th>Description</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Reviewed By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r=>(
                    <tr key={r.id}>
                      <td><strong className="mono">{r.sampleId}</strong></td>
                      <td>{r.requestedByName}</td>
                      <td style={{fontSize:11}}>{r.changeType?.replace('_',' ')}</td>
                      <td style={{maxWidth:200,overflow:'hidden',
                        textOverflow:'ellipsis',fontSize:11}}>{r.description}</td>
                      <td style={{fontSize:11,color:'#888'}}>
                        {new Date(r.submittedAt).toLocaleDateString()}
                      </td>
                      <td>{statusBadge(r.status)}</td>
                      <td style={{fontSize:11,color:'#888'}}>{r.reviewedBy||'—'}</td>
                      <td style={{display:'flex',gap:4}}>
                        <button className="btn btn-sm"
                          onClick={()=>{setReviewModal(r);setReviewNote('');}}>
                          👁 Review
                        </button>
                        {r.status==='pending' && (
                          <>
                            <button className="btn btn-sm btn-success"
                              onClick={()=>handleApprove(r)}>✓</button>
                            <button className="btn btn-sm btn-danger"
                              onClick={()=>handleReject(r)}>✗</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {reviewModal && (
        <div className="modal-overlay" onClick={()=>setReviewModal(null)}>
          <div className="modal" style={{width:560}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h2>👁 Review Change Request</h2>
              <button className="modal-close" onClick={()=>setReviewModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                <div><span style={{fontSize:10,color:'#888'}}>Sample:</span>
                  <div style={{fontWeight:700,fontFamily:'monospace'}}>{reviewModal.sampleId}</div></div>
                <div><span style={{fontSize:10,color:'#888'}}>Requested by:</span>
                  <div style={{fontWeight:600}}>{reviewModal.requestedByName}</div></div>
                <div><span style={{fontSize:10,color:'#888'}}>Change type:</span>
                  <div>{reviewModal.changeType?.replace(/_/g,' ')}</div></div>
                <div><span style={{fontSize:10,color:'#888'}}>Submitted:</span>
                  <div>{new Date(reviewModal.submittedAt).toLocaleString()}</div></div>
              </div>

              <div style={{background:'#f8f9ff',border:'1px solid #e0e4ec',
                borderRadius:4,padding:'8px 12px',marginBottom:12,fontSize:12}}>
                <strong>Description:</strong> {reviewModal.description}
              </div>

              {reviewModal.proposedChanges && Object.keys(reviewModal.proposedChanges).length>0 && (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#555',
                    textTransform:'uppercase',marginBottom:4}}>Proposed Changes</div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                    <thead><tr style={{background:'#f0f4ff'}}>
                      <th style={{padding:'4px 8px',textAlign:'left'}}>Field</th>
                      <th style={{padding:'4px 8px',textAlign:'left'}}>Current</th>
                      <th style={{padding:'4px 8px',textAlign:'left'}}>Proposed</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(reviewModal.proposedChanges).map(([k,v])=>(
                        <tr key={k} style={{borderBottom:'1px solid #eef0f8'}}>
                          <td style={{padding:'3px 8px',fontWeight:600}}>{k}</td>
                          <td style={{padding:'3px 8px',color:'#b83232'}}>
                            {String(reviewModal.currentValues?.[k]??'—')}
                          </td>
                          <td style={{padding:'3px 8px',color:'#1a6b42',fontWeight:600}}>
                            {String(v)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {reviewModal.note && (
                <div style={{background:'#fffbee',border:'1px solid #e0c060',
                  borderRadius:4,padding:'8px 12px',marginBottom:12,fontSize:12}}>
                  <strong>Requester note:</strong> {reviewModal.note}
                </div>
              )}

              {reviewModal.status==='pending' && (
                <div className="form-group">
                  <label>Review Note (optional)</label>
                  <textarea value={reviewNote} onChange={e=>setReviewNote(e.target.value)}
                    rows={2} placeholder="Add a note to the requester..."
                    style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',
                      fontSize:12,width:'100%',resize:'vertical',fontFamily:'inherit',outline:'none'}}/>
                </div>
              )}

              {reviewModal.status!=='pending' && (
                <div style={{background:reviewModal.status==='approved'?'#d4edda':'#f8d7da',
                  borderRadius:4,padding:'8px 12px',fontSize:12}}>
                  <strong>{reviewModal.status==='approved'?'✅ Approved':'❌ Rejected'}</strong>
                  {' by '}{reviewModal.reviewedBy}
                  {reviewModal.reviewNote && ` — "${reviewModal.reviewNote}"`}
                </div>
              )}
            </div>
            <div className="modal-footer">
              {reviewModal.status==='pending' ? (
                <>
                  <button className="btn btn-success"
                    onClick={()=>handleApprove(reviewModal)}>
                    ✅ Approve & Apply Changes
                  </button>
                  <button className="btn btn-danger"
                    onClick={()=>handleReject(reviewModal)}>
                    ❌ Reject
                  </button>
                  <button className="btn" onClick={()=>setReviewModal(null)}>Cancel</button>
                </>
              ) : (
                <button className="btn" onClick={()=>setReviewModal(null)}>Close</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── SettingsTab ───────────────────────────────────────────────────────────────
function SettingsTab({ toast }) {
  const [counters, setCounters] = React.useState(() => db.getCounters());
  const [form, setForm] = React.useState(() => {
    const c = db.getCounters();
    return {
      nextSampleNum:   String(c.nextSampleNum),
      nextInvoiceNum:  String(c.nextInvoiceNum),
      nextWorkOrder:   String(c.nextWorkOrder),
      samplePrefix:    c.samplePrefix    ?? '',
      invoicePrefix:   c.invoicePrefix   ?? '26-',
      workOrderPrefix: c.workOrderPrefix ?? '26-0',
      samplePad:       String(c.samplePad    ?? 0),
      invoicePad:      String(c.invoicePad   ?? 4),
      workOrderPad:    String(c.workOrderPad ?? 3),
    };
  });
  const [resetModal,    setResetModal]    = React.useState(false);
  const [resetConfirm,  setResetConfirm]  = React.useState('');
  const [resetOptions,  setResetOptions]  = React.useState({
    samples:    true,
    invoices:   true,
    approvals:  true,
    unitPrefs:  false,
    lists:      false,
    users:      false,
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const saveCounters = () => {
    const sn  = parseInt(form.nextSampleNum);
    const inv = parseInt(form.nextInvoiceNum);
    const wo  = parseInt(form.nextWorkOrder);
    if (isNaN(sn) || isNaN(inv) || isNaN(wo)) {
      toast('error', 'All values must be valid numbers'); return;
    }
    if (sn < 1 || inv < 1 || wo < 1) {
      toast('error', 'Values must be greater than 0'); return;
    }
    const updated = {
      nextSampleNum: sn, nextInvoiceNum: inv, nextWorkOrder: wo,
      samplePrefix:    form.samplePrefix,
      invoicePrefix:   form.invoicePrefix,
      workOrderPrefix: form.workOrderPrefix,
      samplePad:    parseInt(form.samplePad)    || 0,
      invoicePad:   parseInt(form.invoicePad)   || 4,
      workOrderPad: parseInt(form.workOrderPad) || 3,
    };
    syncedSet('sl_counters', updated);
    setCounters(updated);
    // Show previews
    const sp  = form.samplePrefix    || '';
    const ip  = form.invoicePrefix   || '26-';
    const wop = form.workOrderPrefix || '26-0';
    const spd = parseInt(form.samplePad)    || 0;
    const ipd = parseInt(form.invoicePad)   || 4;
    const wpd = parseInt(form.workOrderPad) || 3;
    const fmtSample  = spd  ? `${sp}${String(sn).padStart(spd,'0')}`  : `${sp}${sn}`;
    const fmtInv     = ipd  ? `${ip}${String(inv).padStart(ipd,'0')}` : `${ip}${inv}`;
    const fmtWO      = wpd  ? `${wop}${String(wo).padStart(wpd,'0')}` : `${wop}${wo}`;
    toast('success', `Saved — next: Sample ${fmtSample} · Invoice ${fmtInv} · WO ${fmtWO}`);
  };

  const [resetting, setResetting] = React.useState(false);
  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    if (resetConfirm !== 'DELETE') {
      toast('error', 'Type DELETE to confirm');
      setResetting(false);
      return;
    }
    const keys = [];
    if (resetOptions.samples)   keys.push('sl_samples', 'sl_counters');
    if (resetOptions.invoices)  keys.push('sl_invoices');
    if (resetOptions.approvals) keys.push('sl_approvals');
    if (resetOptions.unitPrefs) keys.push('sl_unit_prefs','sl_method_prefs',
      'sl_custom_units','sl_custom_methods','sl_param_meta','sl_param_areas');
    if (resetOptions.lists)     keys.push('sl_list_testcategories','sl_list_sampleforms',
      'sl_list_sources','sl_list_technicians');
    if (resetOptions.users)     keys.push('sl_users','sl_session');

    // Set no-seed flag BEFORE clearing
    if (resetOptions.samples) {
      syncedSet('sl_no_seed', '1');
      keys.push('sl_no_seed'); // we'll re-set it below after server clear
    }

    // 1. Clear in-memory cache immediately
    clearCacheKeys(keys);

    // Pause background sync immediately
    pauseSync();

    // Build counters object
    const counters = resetOptions.samples ? {
      nextSampleNum:   parseInt(form.nextSampleNum)   || 86725,
      nextInvoiceNum:  parseInt(form.nextInvoiceNum)  || 1926,
      nextWorkOrder:   parseInt(form.nextWorkOrder)   || 130,
      samplePrefix:    form.samplePrefix    || '',
      invoicePrefix:   form.invoicePrefix   || '26-',
      workOrderPrefix: form.workOrderPrefix || '26-0',
      samplePad:    parseInt(form.samplePad)    || 0,
      invoicePad:   parseInt(form.invoicePad)   || 4,
      workOrderPad: parseInt(form.workOrderPad) || 3,
    } : null;

    // 2. Nuke server
    const SERVER = SERVER_URL;
    const allKeys = [
      'sl_samples','sl_invoices','sl_counters','sl_approvals',
      'sl_users','sl_list_testcategories','sl_list_sampleforms',
      'sl_list_sources','sl_list_technicians','sl_param_areas',
      'sl_param_meta','sl_unit_prefs','sl_method_prefs',
      'sl_custom_units','sl_custom_methods','sl_api_settings',
      'sl_no_seed','sl_hours_log','sl_pay_notices','sl_pricing',
    ];
    const deleteSet = new Set(keys);
    // Always keep users (unless explicitly deleting) and always re-set no_seed after
    const keepKeys = allKeys.filter(k => !deleteSet.has(k));
    // Ensure sl_no_seed is always set after nuke regardless
    if (!keepKeys.includes('sl_no_seed')) keepKeys.push('sl_no_seed');

    try {
      console.log('[Reset] Calling nuke on:', SERVER, 'keepKeys:', keepKeys);
      const res = await fetch(`${SERVER}/nuke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keepKeys }),
      });
      const data = await res.json();
      console.log('[Reset] Nuke response:', data);
      if (!data.success) throw new Error('Nuke failed: ' + JSON.stringify(data));

      // Set no-seed and counters directly on server
      if (resetOptions.samples) {
        await fetch(`${SERVER}/sync/sl_no_seed`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: '1' }),
        });
        await fetch(`${SERVER}/sync/sl_counters`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: counters }),
        });
      }

      toast('success', 'Data deleted. Reloading...');
      setResetModal(false);
      setResetConfirm('');
      setTimeout(() => window.location.href = window.location.href, 1000);

    } catch(e) {
      toast('error', `Reset failed: ${e.message}`);
      setResetting(false);
      return;
    }
  };

  const ALL_RESET_OPTS = [
    { key:'samples',   label:'All Samples & Results',  icon:'🔬', danger:true,
      desc:'Deletes every sample, all entered results, and resets counters to your configured starting numbers' },
    { key:'invoices',  label:'All Invoices',            icon:'💰', danger:true,
      desc:'Deletes all invoices and billing records' },
    { key:'approvals', label:'Approval Request History',icon:'📋', danger:false,
      desc:'Clears all pending and completed approval requests' },
    { key:'unitPrefs', label:'Unit & Method Preferences',icon:'🧪',danger:false,
      desc:'Resets saved unit/method preferences, custom units/methods, and parameter meta overrides' },
    { key:'lists',     label:'Dropdown Lists',          icon:'📝', danger:false,
      desc:'Resets test categories, sample forms, sources, and technician lists to defaults' },
    { key:'users',     label:'User Accounts',           icon:'👥', danger:true,
      desc:'Deletes all users and sessions — you will be logged out (default admin/admin123 will be restored)' },
  ];

  return (
    <>
      <div className="content" style={{display:'grid',gridTemplateColumns:'1fr 1fr',
        gap:16,alignItems:'start'}}>

        {/* ── Starting Numbers ── */}
        <div className="panel">
          <div className="panel-header">🔢 Starting Numbers</div>
          <div className="panel-body">
            <p style={{fontSize:12,color:'#666',marginBottom:14,lineHeight:1.6}}>
              Set the next number to use for new samples, invoices, and work orders.
              Only increase these — setting them lower than existing records may cause duplicates.
            </p>

            {/* Counter rows */}
            {[
              { label:'Sample Number', icon:'🔬',
                prefixKey:'samplePrefix', numKey:'nextSampleNum', padKey:'samplePad',
                current: counters.nextSampleNum,
                hint:'Leave prefix empty for plain numbers like 86725' },
              { label:'Invoice Number', icon:'💰',
                prefixKey:'invoicePrefix', numKey:'nextInvoiceNum', padKey:'invoicePad',
                current: counters.nextInvoiceNum,
                hint:'e.g. prefix "26-" + number 1926 → 26-1926' },
              { label:'Work Order Number', icon:'📋',
                prefixKey:'workOrderPrefix', numKey:'nextWorkOrder', padKey:'workOrderPad',
                current: counters.nextWorkOrder,
                hint:'e.g. prefix "26-0" + number 130 → 26-0130' },
            ].map(f => {
              const prefix = form[f.prefixKey] || '';
              const num    = form[f.numKey]    || '';
              const pad    = parseInt(form[f.padKey]) || 0;
              const preview = num
                ? (pad > 0
                  ? `${prefix}${String(num).padStart(pad,'0')}`
                  : `${prefix}${num}`)
                : '—';
              return (
                <div key={f.label} style={{marginBottom:14,padding:'10px 12px',
                  background:'#f8f9ff',borderRadius:6,border:'1px solid #e0e4ec'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#1e3060',
                    marginBottom:8}}>{f.icon} {f.label}</div>
                  <div style={{display:'grid',
                    gridTemplateColumns:'120px 100px 80px auto',
                    gap:8,alignItems:'center'}}>
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:'#888',
                        textTransform:'uppercase',marginBottom:3}}>Prefix</div>
                      <input value={form[f.prefixKey]}
                        onChange={e=>set(f.prefixKey, e.target.value)}
                        placeholder="e.g. 26-"
                        style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,
                          padding:'5px 8px',fontSize:12,fontFamily:'monospace',
                          outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:'#888',
                        textTransform:'uppercase',marginBottom:3}}>Next Number</div>
                      <input type="number" min="1" value={form[f.numKey]}
                        onChange={e=>set(f.numKey, e.target.value)}
                        style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,
                          padding:'5px 8px',fontSize:12,fontFamily:'monospace',
                          fontWeight:700,outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:'#888',
                        textTransform:'uppercase',marginBottom:3}}>Zero Pad</div>
                      <input type="number" min="0" max="8" value={form[f.padKey]}
                        onChange={e=>set(f.padKey, e.target.value)}
                        title="Number of digits (0 = no padding)"
                        style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,
                          padding:'5px 8px',fontSize:12,fontFamily:'monospace',
                          outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div style={{paddingTop:14}}>
                      <div style={{fontSize:9,fontWeight:700,color:'#888',
                        textTransform:'uppercase',marginBottom:3}}>Preview</div>
                      <span style={{fontFamily:'monospace',fontWeight:700,
                        fontSize:14,color:'#1a6b42',
                        background:'#e8f4e8',padding:'4px 10px',
                        borderRadius:4}}>{preview}</span>
                    </div>
                  </div>
                  <div style={{fontSize:10,color:'#aaa',marginTop:4}}>{f.hint}</div>
                </div>
              );
            })}

            <button className="btn btn-primary" onClick={saveCounters}
              style={{padding:'8px 24px'}}>
              💾 Save Starting Numbers
            </button>

            <div style={{marginTop:12,padding:'8px 12px',background:'#fff8e8',
              border:'1px solid #e0c060',borderRadius:4,fontSize:11,color:'#7a5a00'}}>
              💡 <strong>Tip:</strong> Set these before going live. For example, if your last
              FileMaker sample was #86900, set Next Sample # to 86901.
            </div>
          </div>
        </div>

        {/* ── Data Reset ── */}
        <div className="panel">
          <div className="panel-header" style={{background:'#8b1a1a'}}>
            🗑 Reset / Clear Data
          </div>
          <div className="panel-body">
            <p style={{fontSize:12,color:'#666',marginBottom:14,lineHeight:1.6}}>
              Permanently delete selected data. <strong>This cannot be undone.</strong>{' '}
              Make a backup first from the 💾 Backup & Restore tab.
            </p>
            <button
              style={{background:'#b83232',color:'white',border:'none',borderRadius:5,
                padding:'9px 22px',cursor:'pointer',fontSize:13,fontWeight:700,
                boxShadow:'0 2px 6px rgba(184,50,50,0.3)'}}
              onClick={()=>{setResetModal(true);setResetConfirm('');}}>
              🗑 Open Data Reset Panel
            </button>
          </div>
        </div>
      </div>

      {/* ── Reset Modal ── */}
      {resetModal && (
        <div className="modal-overlay" onClick={()=>setResetModal(false)}>
          <div className="modal" style={{width:560}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header" style={{background:'#8b1a1a'}}>
              <h2>🗑 Data Reset</h2>
              <button className="modal-close" onClick={()=>setResetModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{background:'#fff0f0',border:'1px solid #f0b8b8',borderRadius:6,
                padding:'10px 14px',marginBottom:14,fontSize:12,color:'#7a2020'}}>
                ⚠️ <strong>Warning:</strong> Deleted data cannot be recovered.
                Please download a backup before proceeding.
              </div>

              <div style={{fontSize:11,fontWeight:700,color:'#555',
                textTransform:'uppercase',marginBottom:8}}>
                Select what to delete:
              </div>

              {ALL_RESET_OPTS.map(opt => (
                <label key={opt.key}
                  style={{display:'flex',gap:10,padding:'8px 12px',marginBottom:6,
                    borderRadius:5,cursor:'pointer',
                    border:`1px solid ${resetOptions[opt.key]
                      ? (opt.danger ? '#f0b8b8' : '#aac8ee')
                      : '#e0e4ec'}`,
                    background: resetOptions[opt.key]
                      ? (opt.danger ? '#fff5f5' : '#f0f6ff') : 'white'}}>
                  <input type="checkbox"
                    checked={resetOptions[opt.key]}
                    onChange={e=>setResetOptions(p=>({...p,[opt.key]:e.target.checked}))}
                    style={{accentColor: opt.danger ? '#b83232' : '#1e3060',
                      width:15,height:15,marginTop:2,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,
                      color: opt.danger ? '#8b1a1a' : '#1e3060'}}>
                      {opt.icon} {opt.label}
                      {opt.danger && <span style={{marginLeft:6,fontSize:9,
                        background:'#b83232',color:'white',borderRadius:4,
                        padding:'1px 5px'}}>PERMANENT</span>}
                    </div>
                    <div style={{fontSize:10,color:'#888',marginTop:2}}>
                      {opt.desc}
                    </div>
                  </div>
                </label>
              ))}

              <div style={{marginTop:14,padding:'10px 14px',
                background:'#f8f9ff',borderRadius:5,border:'1px solid #e0e4ec'}}>
                <div style={{fontSize:11,fontWeight:700,color:'#555',marginBottom:6}}>
                  Type <code style={{background:'#fee',color:'#b83232',
                    padding:'1px 4px',borderRadius:3}}>DELETE</code> to confirm:
                </div>
                <input value={resetConfirm}
                  onChange={e=>setResetConfirm(e.target.value)}
                  placeholder="Type DELETE here"
                  style={{width:'100%',border:`2px solid ${resetConfirm==='DELETE'?'#b83232':'#cdd3e4'}`,
                    borderRadius:4,padding:'7px 10px',fontSize:13,outline:'none',
                    fontFamily:'monospace',fontWeight:700,
                    color:resetConfirm==='DELETE'?'#b83232':'#333',
                    boxSizing:'border-box'}}/>
              </div>
            </div>
            <div className="modal-footer">
              <button
                disabled={resetting || resetConfirm !== 'DELETE' ||
                  !Object.values(resetOptions).some(Boolean)}
                onClick={handleReset}
                style={{background: resetConfirm==='DELETE' ? '#b83232' : '#ccc',
                  color:'white',border:'none',borderRadius:4,
                  padding:'9px 24px',cursor: resetConfirm==='DELETE' ? 'pointer' : 'not-allowed',
                  fontSize:13,fontWeight:700,opacity: resetConfirm==='DELETE' ? 1 : 0.6}}>
                {resetting ? '⏳ Deleting...' : '🗑 Delete Selected Data'}
              </button>
              <button className="btn" onClick={()=>setResetModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
